import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const [statusFilter, setStatusFilter] = useState<'all' | '连载中' | '已完结'>('all');

  const currentCategory = categories.find((c) => c.slug === slug);

  const filtered = useMemo(() => {
    let result = [...comics];
    if (slug && slug !== 'all') {
      result = result.filter((c) => c.categories.includes(slug));
    }
    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [slug, sortBy, statusFilter]);

  const catName = currentCategory?.name || '全部漫画';
  const pageTitle = `${catName} - 韩国漫画分类`;

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: `073漫画网 ${catName} 分类，海量无删减韩国漫画免费在线阅读。`,
    url: 'http://aduvet.com/category/all',
    isPartOf: {
      '@type': 'WebSite',
      name: '073漫画网',
      url: 'http://aduvet.com/',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://aduvet.com/' },
      { '@type': 'ListItem', position: 2, name: catName, item: 'http://aduvet.com/category/all' },
    ],
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={`073漫画网${catName}分类，提供海量无删减韩国漫画下拉式免费在线阅读，每日更新，高清全彩。`}
        keywords={`韩国漫画,${catName},无删减漫画,下拉式韩漫,免费韩漫在线阅读`}
        canonicalUrl={'http://aduvet.com/category/all'}
        jsonLd={{ '@context': 'https://schema.org', '@graph': [categoryJsonLd, breadcrumbJsonLd] }}
      />

      <Navbar />

      <main className="pt-16">
        <nav className="bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-800 font-medium">{catName}</li>
            </ol>
          </div>
        </nav>

        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              {catName}
            </h1>
            <p className="text-foreground-500 text-sm mt-1">
              共 {filtered.length} 部漫画
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <div className="flex flex-wrap gap-2">
                {[{ slug: 'all', name: '全部' }, ...categories].map((cat) => (
                  <Link
                    key={cat.slug}
                    to="/category/all"
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
                      (slug || 'all') === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                {[
                  { key: 'popular' as const, label: '人气' },
                  { key: 'latest' as const, label: '最新' },
                  { key: 'rating' as const, label: '评分' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">状态：</span>
                {[
                  { key: 'all' as const, label: '全部' },
                  { key: '连载中' as const, label: '连载中' },
                  { key: '已完结' as const, label: '已完结' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setStatusFilter(s.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                      statusFilter === s.key
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {filtered.map((comic) => (
                  <ComicCard
                    key={comic.slug}
                    slug={comic.slug}
                    title={comic.title}
                    author={comic.author}
                    cover={comic.cover}
                    rating={comic.rating}
                    status={comic.status}
                    latestChapter={comic.chapters[0]?.number}
                    variant="compact"
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无符合条件的漫画</p>
              </div>
            )}

            {filtered.length > 10 && (
              <div className="mt-10 flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((p) => (
                  <button
                    key={p}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      p === 1
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-more-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}